import { dictionaryApi } from './dictionaryClient';
import { normalizeBhedBikalpa, type BhedBikalpa } from './bhedBikalpaApi';

export type BarnaType = 'swar' | 'byanjan' | 'ayogabaha';

export type BhedSummary = {
  id: number;
  barnaId: number;
  matra: string;
  swor: string;
  anunasik: 'anunasik' | 'ananunasik';
  text: string;
  bikalpas?: BhedBikalpa[];
};

export type SorakhariSummary = {
  id: number;
  barnaId: number;
  text: string;
  combination: Array<{
    barna_id: number;
    bhed_id: number | null;
    order: number;
  }>;
};

export type Barna = {
  id: number;
  name: string;
  description: string;
  type: BarnaType;
  bheds?: BhedSummary[];
  vyanjanSorakhari?: SorakhariSummary[];
};

export type CreateBarnaBody = {
  name: string;
  description: string;
  type: BarnaType;
};

export type UpdateBarnaBody = Partial<CreateBarnaBody>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function toStr(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function normalizeBhedSummary(raw: unknown): BhedSummary | null {
  if (!isRecord(raw)) return null;
  const id = toNum(raw.id);
  const barnaId = toNum(raw.barnaId ?? raw.barna_id);
  if (id == null || barnaId == null) return null;
  const nested = raw.bhed_bikalpa ?? raw.bhedBikalpas ?? raw.bikalpas;
  const bikalpas = Array.isArray(nested)
    ? nested.map(normalizeBhedBikalpa).filter((x): x is BhedBikalpa => x != null)
    : [];
  return {
    id,
    barnaId,
    matra: toStr(raw.matra),
    swor: toStr(raw.swor),
    anunasik: toStr(raw.anunasik) === 'ananunasik' ? 'ananunasik' : 'anunasik',
    text: toStr(raw.text),
    ...(bikalpas.length > 0 ? { bikalpas } : {}),
  };
}

function normalizeSorakhariSummary(raw: unknown): SorakhariSummary | null {
  if (!isRecord(raw)) return null;
  const id = toNum(raw.id);
  const barnaId = toNum(raw.barnaId ?? raw.barna_id);
  if (id == null || barnaId == null) return null;
  const combination = Array.isArray(raw.combination)
    ? raw.combination
        .map((row) => {
          if (!isRecord(row)) return null;
          const cBarnaId = toNum(row.barna_id ?? row.barnaId);
          const cBhedId = toNum(row.bhed_id ?? row.bhedId);
          const cOrder = toNum(row.order);
          if (cBarnaId == null || cOrder == null) return null;
          return { barna_id: cBarnaId, bhed_id: cBhedId, order: cOrder };
        })
        .filter((x): x is { barna_id: number; bhed_id: number | null; order: number } => x != null)
    : [];
  return {
    id,
    barnaId,
    text: toStr(raw.text),
    combination,
  };
}

export function normalizeBarna(raw: unknown): Barna | null {
  if (!isRecord(raw)) return null;
  const id = toNum(raw.id);
  if (id == null) return null;
  const type = toStr(raw.type) as BarnaType;
  return {
    id,
    name: toStr(raw.name),
    description: toStr(raw.description),
    type: type === 'swar' || type === 'byanjan' || type === 'ayogabaha' ? type : 'swar',
    bheds: Array.isArray(raw.bheds) ? raw.bheds.map(normalizeBhedSummary).filter((x): x is BhedSummary => x != null) : [],
    vyanjanSorakhari: Array.isArray(raw.vyanjan_sorakhari)
      ? raw.vyanjan_sorakhari.map(normalizeSorakhariSummary).filter((x): x is SorakhariSummary => x != null)
      : Array.isArray(raw.sorakhari)
        ? raw.sorakhari.map(normalizeSorakhariSummary).filter((x): x is SorakhariSummary => x != null)
        : [],
  };
}

function normalizeList(data: unknown): Barna[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizeBarna).filter((x): x is Barna => x != null);
}

export async function listBarna(): Promise<Barna[]> {
  const res = await dictionaryApi.get<unknown>('/barna');
  return normalizeList(res.data);
}

export async function getBarna(id: number): Promise<Barna> {
  const res = await dictionaryApi.get<unknown>(`/barna/${id}`);
  const row = normalizeBarna(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid barna response');
  return row;
}

export async function createBarna(body: CreateBarnaBody): Promise<Barna> {
  const res = await dictionaryApi.post<unknown>('/barna', body);
  const row = normalizeBarna(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid barna response');
  return row;
}

export async function updateBarna(id: number, body: UpdateBarnaBody): Promise<Barna> {
  const res = await dictionaryApi.patch<unknown>(`/barna/${id}`, body);
  const row = normalizeBarna(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid barna response');
  return row;
}

export async function deleteBarna(id: number): Promise<void> {
  await dictionaryApi.delete(`/barna/${id}`);
}
